import Quill, { QuillOptions } from 'quill';
import { ThemeOptions } from 'quill/core/theme';
import { Context } from 'quill/modules/keyboard';
import Emitter from 'quill/core/emitter';
import Toolbar from 'quill/modules/toolbar';
import BaseTheme, { BaseTooltip } from 'quill/themes/base';
import icons from 'quill/ui/icons';
import LinkBlot from 'quill/formats/link';
import { Bounds, Range } from 'quill/core/selection';
import store from 'app/store';
import { selectDocumentsEntities } from 'app/store/DMSDocuments/DMSDocuments.selectors';

export interface ITooltipOptions {
  showPreviewButton: boolean;
}

export interface CCThemeOptions extends ThemeOptions {
  store?: typeof store;
  tooltip?: ITooltipOptions;
}

export interface CCQuillOptions extends QuillOptions {
  showPreviewButton?: boolean;
}

class CCThemeTooltip extends BaseTooltip {
  linkRange: Range | undefined = undefined;
  clickedLink: HTMLAnchorElement | null = null;
  textbox: HTMLInputElement;

  private readonly tooltipOptions?: ITooltipOptions;

  // DOM Elements - Now publicly accessible
  // Making these public allows external code to attach listeners or read state.
  public readonly preview: HTMLAnchorElement;
  public readonly previewDocumentLink: HTMLAnchorElement;
  public readonly previewPageNumLink: HTMLAnchorElement;
  public readonly editButton: HTMLButtonElement;
  public readonly previewButton: HTMLButtonElement | null;

  // DOM Elements - Kept private for internal class use
  private readonly helperText: HTMLElement;
  private readonly cancelButton: HTMLButtonElement;
  private readonly saveButton: HTMLButtonElement;
  private readonly removeButton: HTMLButtonElement;

  private static TEMPLATE(showPreviewButton = false): string {
    return [
      '<div class="ql-tooltip-container">',
        '<div class="ql-preview-group">',
          '<a class="ql-preview" id="js_previewLink" rel="noopener noreferrer" target="_blank" href="about:blank"></a>',
          '<a class="ql-preview ql-preview-document-name ql-hidden" id="js_previewDocumentLink" rel="noopener noreferrer" target="_blank" href="about:blank"></a>',
          '<a class="ql-preview ql-preview-page-num ql-hidden" id="js_previewPageNumLink" rel="noopener noreferrer" target="_blank" href="about:blank"></a>',
        '</div>',
        '<div class="ql-input-group">',
          '<input type="text" data-formula="e=mc^2" data-link="https://quilljs.com" data-video="Embed URL">',
          '<span class="ql-helper-text hidden" id="js_helperText">URL is not valid invalid</span>',
        '</div>',
        '<div class="ql-action-group">',
          '<button type="button" class="ql-action-cancel" id="js_actionCancel">Cancel</button>',
          '<button type="button" class="ql-action-save" id="js_actionSave">Save</button>',
          showPreviewButton ? `<button title="Preview" type="button" class="ql-action-preview hidden" id="js_actionPreview"><i class="icon icon-preview"></i></button>` : '',
          `<button title="Edit" type="button" class="ql-action-edit" id="js_actionEdit"><i class="icon icon-edit"></i></button>`,
          `<button title="Remove" type="button" class="ql-action-remove" id="js_actionRemove"><i class="icon icon-delete"></i></button>`,
        '</div>',
      '</div>'
    ].join('');
  }

  constructor(quill: Quill, bounds: HTMLElement, options?: ITooltipOptions) {
    super(quill, bounds);
    this.tooltipOptions = options;

    this.root.innerHTML = CCThemeTooltip.TEMPLATE(
      this.tooltipOptions?.showPreviewButton ?? false,
    );

    this.preview = this.root.querySelector('#js_previewLink') as HTMLAnchorElement;
    this.previewDocumentLink = this.root.querySelector('#js_previewDocumentLink') as HTMLAnchorElement;
    this.previewPageNumLink = this.root.querySelector('#js_previewPageNumLink') as HTMLAnchorElement;
    this.previewButton = this.root.querySelector('#js_actionPreview') as HTMLButtonElement | null;
    this.editButton = this.root.querySelector('#js_actionEdit') as HTMLButtonElement;

    this.textbox = this.root.querySelector('input[type="text"]') as HTMLInputElement;
    this.helperText = this.root.querySelector('#js_helperText') as HTMLElement;
    this.cancelButton = this.root.querySelector('#js_actionCancel') as HTMLButtonElement;
    this.saveButton = this.root.querySelector('#js_actionSave') as HTMLButtonElement;
    this.removeButton = this.root.querySelector('#js_actionRemove') as HTMLButtonElement;

    this.cancelButton.addEventListener('click', this.handleCancel.bind(this));
    this.saveButton.addEventListener('click', this.handleSave.bind(this));
    this.editButton.addEventListener('click', this.handleEdit.bind(this));
    this.removeButton.addEventListener('click', this.handleRemove.bind(this));
  }

  listen(): void {
    this.quill.on(
      Emitter.events.SELECTION_CHANGE,
      this.handleSelectionChange.bind(this),
    );
  }

  position(reference: Bounds) {
    const left = reference.left + reference.width / 2 - this.root.offsetWidth / 2;
    // root.scrollTop should be 0 if scrollContainer !== root
    const top = reference.bottom + this.quill.root.scrollTop;
    this.root.style.left = `${left}px`;
    this.root.style.top = `${top}px`;
    this.root.classList.remove('ql-flip');

    // Use the editor container as the bounding box so the tooltip never renders outside the editor
    const containerBounds = this.quill.container.getBoundingClientRect();
    // Read the tooltip bounds after applying initial position
    let rootBounds = this.root.getBoundingClientRect();
    let shift = 0;

    // If tooltip overflows to the right, shift it left and recompute bounds
    if (rootBounds.right > containerBounds.right) {
      shift = containerBounds.right - rootBounds.right;
      this.root.style.left = `${left + shift}px`;
      rootBounds = this.root.getBoundingClientRect();
    }

    // If tooltip overflows to the left, shift it right and recompute bounds
    if (rootBounds.left < containerBounds.left) {
      shift = containerBounds.left - rootBounds.left;
      this.root.style.left = `${left + shift}px`;
      rootBounds = this.root.getBoundingClientRect();
    }

    // If tooltip overflows bottom of the container, flip it above the reference and recompute bounds
    if (rootBounds.bottom > containerBounds.bottom) {
      const height = rootBounds.bottom - rootBounds.top;
      const verticalShift = reference.bottom - reference.top + height;
      this.root.style.top = `${top - verticalShift}px`;
      this.root.classList.add('ql-flip');
      rootBounds = this.root.getBoundingClientRect();
    }

    return shift;
  }

  private handleSelectionChange(range: Range | null, _oldRange: Range | null, source: string): void {
    if (!range) return;
    if (range.length === 0 && source === Emitter.sources.USER) {
      const [link, offset] = this.quill.scroll.descendant(
        LinkBlot,
        range.index,
      );
      if (link) {
        this.clickedLink = link.domNode as HTMLAnchorElement;
        this.linkRange = new Range(range.index - offset, link.length());
        const linkUrl = LinkBlot.formats(link.domNode);
        this.updatePreview(linkUrl as string);
        this.show();
        const bounds = this.quill.getBounds(this.linkRange);
        if (bounds) this.position(bounds);
        return;
      }
    }
    this.hideTooltip();
  }

  private updatePreview(linkUrl: string): void {
    if ( this.isInternalLink(linkUrl) ) {
      const linkData = this.getInternalLinkData(linkUrl);
      if (linkData) {
        const store = (this.quill.options as CCThemeOptions).store;
        if ( store ) {
          this.preview.classList.add('ql-hidden');
          const state = store.getState();
          const documentsEntities = selectDocumentsEntities(state);
          const documentEntity = documentsEntities[linkData.documentId];
          const toDocument = `${window.location.origin}/admin/cases/${linkData.caseId}/documents/${linkData.documentId}`;
          this.previewDocumentLink.classList.remove('ql-hidden');
          const documentName = documentEntity ? documentEntity.name : `[Document ${linkData.documentId}]`;
          this.previewDocumentLink.textContent = documentName;
          this.previewDocumentLink.setAttribute('href', toDocument);
          this.previewDocumentLink.setAttribute('title', documentName);
          if ( linkData.pageNum !== undefined ) {
            const toPage = `${toDocument}/${linkData.pageNum}`;
            this.previewPageNumLink.classList.remove('ql-hidden');
            this.previewPageNumLink.textContent = `Page ${linkData.pageNum}`;
            this.previewPageNumLink.setAttribute('href', toPage);
          } else {
            this.previewPageNumLink.classList.add('ql-hidden');
          }
        }
      }
    } else {
      this.previewDocumentLink.classList.add('ql-hidden');
      this.previewPageNumLink.classList.add('ql-hidden');
      this.preview.classList.remove('ql-hidden');
    }
    
    this.preview.textContent = linkUrl;
    this.preview.setAttribute('href', linkUrl);

    if (this.previewButton) {
      if (this.isInternalLink(linkUrl)) {
        this.previewButton.classList.remove('hidden');
      } else {
        this.previewButton.classList.add('hidden');
      }
    }
  }

  private hideTooltip(): void {
    this.hide();
    this.helperText.classList.add('hidden');
    if (this.previewButton) {
      this.previewButton.classList.add('hidden');
    }
  }

  private handleCancel(event: Event): void {
    // // Simplified logic: always show if there's a link selected, otherwise hide/stay hidden.
    // if (this.linkRange) {
    //   this.show();
    //   this.helperText.classList.add('hidden');
    // } else {
    //   this.hide();
    // }
    this.hide();
    this.show();

    event.preventDefault();
  }

  private handleSave(event: Event): void {
    event.preventDefault();
    if (!this.textbox) return;

    const value = this.textbox.value.trim();

    // Simple URL validation
    if (!isUrl(value)) {
      this.helperText.classList.remove('hidden');
      return;
    }

    this.helperText.classList.add('hidden');
    this.save();
  }

  private handleEdit(event: Event): void {
    // Check if linkRange exists to ensure we're editing a known link
    if (!this.preview || !this.linkRange) return;
    // Use the link's text content for editing
    this.edit('link', this.preview.textContent);
    event.preventDefault();
  }

  private handleRemove(event: Event): void {
    if (this.linkRange) {
      this.restoreFocus();
      // Unformat the link in the selected range
      this.quill.formatText(this.linkRange, 'link', false, Emitter.sources.USER);
      this.linkRange = undefined; // Clear the range after removal
    }
    this.hideTooltip(); // Use the consolidated hide method
    event.preventDefault();
  }

  // Only allow links like /admin/cases/{caseId}/documents/{documentId}/{pageNum}
  // Capture caseId, documentId, and optional pageNum; allow optional trailing slash.
  private readonly internalPathRegex = /^\/admin\/cases\/(\d+)\/documents\/(\d+)(?:\/(\d+))?\/?$/;

  private isInternalLink(value: string): boolean {
    try {
      const url = new URL(value, window.location.origin);
      // Test the path part of the URL
      return this.internalPathRegex.test(url.pathname);
    } catch {
      // If value is not a full URL (i.e., it's a relative path)
      return this.internalPathRegex.test(value);
    }
  }
  private getInternalLinkData(value: string): { caseId: number; documentId: number; pageNum?: number } | null {
    // If a full URL was passed, extract the pathname first so the regex can match.
    try {
      const url = new URL(value, window.location.origin);
      value = url.pathname;
    } catch {
      // value may already be a pathname; continue
    }

    const match = value.match(this.internalPathRegex);
    if (match) {
      const caseId = match[1];
      const documentId = match[2];
      const pageNum = match[3];
      return { caseId: Number(caseId), documentId: Number(documentId), pageNum: pageNum ? Number(pageNum) : undefined };
    }
    return null;
  }
}

class CCTheme extends BaseTheme {
  constructor(quill: Quill, options: CCThemeOptions) {
    super(quill, options);
    this.quill.container.classList.add('ql-snow');
  }

  extendToolbar(toolbar: Toolbar) {
    if (toolbar.container != null) {
      toolbar.container.classList.add('ql-snow');
      this.buildButtons(toolbar.container.querySelectorAll('button'), icons);
      this.buildPickers(toolbar.container.querySelectorAll('select'), icons);
      // @ts-expect-error
      this.tooltip = new CCThemeTooltip(this.quill, this.options.bounds, this.options.tooltipOptions || {
        showPreviewButton: false
      });
      this.tooltip.root.classList.add('ql-cc-tooltip');
      if (toolbar.container.querySelector('.ql-link')) {
        this.quill.keyboard.addBinding(
          { key: 'k', shortKey: true },
          (_range: Range, context: Context) => {
            toolbar.handlers.link.call(toolbar, !context.format.link);
          },
        );
      }
    }
  }
}

CCTheme.DEFAULTS = Object.assign({}, BaseTheme.DEFAULTS, {
  modules: {
    toolbar: {
      handlers: {
        link(value: string) {
          if (value) {
            const range = this.quill.getSelection();
            if (range == null || range.length === 0) return;
            let preview = this.quill.getText(range);
            if (
              /^\S+@\S+\.\S+$/.test(preview) &&
              preview.indexOf('mailto:') !== 0
            ) {
              preview = `mailto:${preview}`;
            }
            // @ts-expect-error
            const { tooltip } = this.quill.theme;
            tooltip.edit('link', preview);
          } else {
            this.quill.format('link', false, Quill.sources.USER);
          }
        },
      },
    },
  },
} satisfies ThemeOptions);

Quill.register('themes/ссtheme', CCTheme, true);

const isUrl = (url:string) => {
  try { return Boolean(new URL(url)); }
  catch(e){ return false; }
}

